/**
 * A single interactive pill that BOTH shows an outlet's current state
 * AND, when clicked, toggles it. There is no separate "status" + "button"
 * pair any more — the badge IS the control.
 *
 *   Outlet 1: on (since 2026-06-08 09:00)   ← green pill, click → confirm → OFF
 *   Outlet 2: off (since 2026-06-08 09:00)  ← gray pill, click → confirm → ON
 *
 * Disabled (grayed) when the plug is offline or we have no telemetry
 * for the outlet state. Click → confirm modal → single API call
 * (POST /api/plugs/{scu}/outlet/{n}/{on|off}). Server is platform-only.
 *
 * Component name kept (PlugOutletResetButton) so the call sites that
 * already import it don't need rewiring.
 */
import React, { useState } from "react";

/**
 * What each outlet feeds on a Gen5 smartbox plug (Jason, 2026-08-06):
 * outlet 1 is the edge computer, outlet 2 is the Starlink.
 *
 * This is the fleet convention, not a per-device lookup — nothing in
 * `hubspace_plug` records per-outlet loads today. Corroborated by
 * telemetry: across 10 historical outlet-2 switch-offs, boxes stayed
 * reachable and cameras kept capturing (two within 9 minutes), so
 * outlet 2 is neither box power nor the camera. If per-outlet labels
 * ever land in the schema, source this from them instead.
 */
const OUTLET_LOADS: Record<1 | 2, string> = {
  1: "The SmartBox edge computer",
  2: "The Starlink",
};

import { Button } from "../ui/button";
import { useAuth } from "../../contexts/AuthContext";
import { fmtUtc } from "../../lib/format";
import { plugOutletAction, type PlugCommandResponse } from "../../services/plugsApi";

export interface PlugOutletResetButtonProps {
  scu: string;
  /** 1 or 2. */
  outletNum: 1 | 2;
  /** Current on/off state. `null` → unknown (pill renders disabled). */
  outletOn: boolean | null;
  /** ISO timestamp of when the outlet last entered its current state.
   *  Rendered as "(since ...)" alongside the on/off badge. */
  outletSinceUtc?: string | null;
  /** Optional label for the confirmation dialog (e.g. smartbox code). */
  smartboxLabel?: string | null;
  /** Visual variant —
   *    'micro'  — bare text like `1:on` for the inline Plug line,
   *    'inline' — pill (outlet status + since timestamp),
   *    'card'   — bigger pill for the signal-page tile. */
  variant?: "micro" | "inline" | "card";
  /** Plug online state. `false`/`null` → pill disabled (the cloud
   *  command can't reach an offline plug). */
  plugIsOnline?: boolean | null;
  /** Called after a successful flip so the parent can refresh. */
  onSuccess?: (response: PlugCommandResponse) => void;
}

export const PlugOutletResetButton: React.FC<PlugOutletResetButtonProps> = ({
  scu,
  outletNum,
  outletOn,
  outletSinceUtc,
  smartboxLabel,
  variant = "inline",
  plugIsOnline = null,
  onSuccess,
}) => {
  const { token, can } = useAuth();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{
    ok: boolean;
    message: string;
  } | null>(null);

  // Server enforces operator.triage too — hide for non-operators so
  // they don't see a teasing pill.
  if (!can("operator.triage")) {
    // Still render the state as plain text for read-only viewers.
    return (
      <span className="text-sm text-secondary-700">
        {outletOn === null ? "—" : outletOn ? "on" : "off"}
        {outletSinceUtc ? ` (since ${fmtUtc(outletSinceUtc)})` : ""}
      </span>
    );
  }

  const target = smartboxLabel || scu;
  const reachable = plugIsOnline === true;
  const knowState = outletOn === true || outletOn === false;
  const enabled = reachable && knowState;
  const disabledReason = !reachable
    ? plugIsOnline === false
      ? "Plug is offline — command can't reach the device"
      : "Plug status unknown — cannot confirm reachability"
    : !knowState
      ? "Outlet state unknown — wait for the next poll"
      : "";

  // Direction the click flips to.
  const desired: "on" | "off" = outletOn === true ? "off" : "on";

  // Pill styling: green when ON, gray when OFF. Hover ring hints the
  // pill is interactive. Disabled state uses muted neutral.
  const baseTone =
    outletOn === true
      ? "bg-success-50 text-success-800 border-success-300"
      : outletOn === false
        ? "bg-secondary-100 text-secondary-700 border-secondary-300"
        : "bg-secondary-50 text-secondary-500 border-secondary-200";
  const enabledHover = enabled
    ? "hover:ring-2 hover:ring-primary-300 hover:brightness-95 cursor-pointer"
    : "opacity-60 cursor-not-allowed";

  const stateLabel =
    busy
      ? "working…"
      : outletOn === null
        ? "—"
        : outletOn
          ? "on"
          : "off";
  const sinceLabel = outletSinceUtc ? ` (since ${fmtUtc(outletSinceUtc)})` : "";

  const sizeClasses =
    variant === "inline"
      ? "px-2 py-0.5 text-xs"
      : "px-3 py-1 text-sm";

  const run = async (): Promise<void> => {
    if (!token) return;
    setBusy(true);
    setResult(null);
    try {
      const resp = await plugOutletAction(token, scu, outletNum, desired);
      setResult({ ok: resp.success, message: resp.message });
      if (resp.success && onSuccess) onSuccess(resp);
    } catch (e) {
      setResult({
        ok: false,
        message: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  // Micro variant — just `N:on` or `N:off` as bare colored text;
  // designed to drop into the inline Plug status line (e.g.
  // "Plug ● 1:on 2:off 47d 22h"). No dot, no `since`, no border.
  if (variant === "micro") {
    const microTone =
      outletOn === true
        ? "text-success-700"
        : outletOn === false
          ? "text-error-700"
          : "text-secondary-500";
    return (
      <>
        <button
          type="button"
          onClick={() => enabled && !busy && setConfirming(true)}
          disabled={busy || !enabled}
          className={
            "font-semibold underline decoration-dotted decoration-1 underline-offset-2 " +
            microTone +
            " " +
            (enabled
              ? "hover:decoration-solid hover:brightness-110 cursor-pointer"
              : "opacity-60 cursor-not-allowed no-underline")
          }
          title={
            enabled
              ? `Click to turn outlet ${outletNum} ${desired.toUpperCase()} on ${target}`
              : disabledReason
          }
          aria-label={`outlet ${outletNum} ${stateLabel}${enabled ? `, click to turn ${desired.toUpperCase()}` : ""}`}
        >
          {outletNum}:{stateLabel}
        </button>
        {confirming && _renderConfirmModal({
          outletNum, target, desired, busy, setConfirming, run,
        })}
        {result && (
          <span
            className={
              "ml-1 " + (result.ok ? "text-success-700" : "text-error-700")
            }
            title={result.message}
          >
            {result.ok ? "✓" : "✕"}
          </span>
        )}
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => enabled && !busy && setConfirming(true)}
        disabled={busy || !enabled}
        className={
          "inline-flex items-center gap-1 rounded-full border transition-all font-mono " +
          sizeClasses +
          " " +
          baseTone +
          " " +
          enabledHover
        }
        title={
          enabled
            ? `Click to turn outlet ${outletNum} ${desired.toUpperCase()} on ${target}`
            : disabledReason
        }
        aria-label={`outlet ${outletNum} ${stateLabel}${enabled ? `, click to turn ${desired.toUpperCase()}` : ""}`}
      >
        <span
          className={
            "inline-block w-2 h-2 rounded-full " +
            (outletOn === true
              ? "bg-success-500"
              : outletOn === false
                ? "bg-secondary-400"
                : "bg-secondary-300")
          }
          aria-hidden
        />
        <strong>{stateLabel}</strong>
        {sinceLabel && (
          <span className="font-sans font-normal text-[11px] opacity-75">
            {sinceLabel}
          </span>
        )}
      </button>

      {result && (
        <span
          className={
            "ml-2 text-xs " +
            (result.ok ? "text-success-700" : "text-error-700")
          }
          title={result.message}
        >
          {result.ok ? "✓ done" : "✕ failed"}
        </span>
      )}

      {confirming &&
        _renderConfirmModal({
          outletNum, target, desired, busy, setConfirming, run,
        })}
    </>
  );
};

function _renderConfirmModal(args: {
  outletNum: 1 | 2;
  target: string;
  desired: "on" | "off";
  busy: boolean;
  setConfirming: (v: boolean) => void;
  run: () => Promise<void>;
}): React.ReactElement {
  const { outletNum, target, desired, busy, setConfirming, run } = args;
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) setConfirming(false);
      }}
    >
      <div className="bg-white rounded-md shadow-xl max-w-md w-full p-5">
        <h2 className="text-base font-semibold text-secondary-900">
          Turn outlet {outletNum} {desired.toUpperCase()}?
        </h2>
        <p className="text-sm text-secondary-700 mt-2">
          About to turn outlet <strong>{outletNum}</strong> on{" "}
          <strong>{target}</strong>{" "}
          <strong>{desired.toUpperCase()}</strong> and leave it that way.{" "}
          <strong>{OUTLET_LOADS[outletNum]}</strong> will{" "}
          {desired === "off" ? "lose power" : "be powered on"}.
        </p>
        {outletNum === 1 && desired === "off" && (
          <p className="text-sm text-error-700 mt-2 font-semibold">
            This powers down the SmartBox itself. It will drop off the
            network and cannot be turned back on from here unless the plug
            stays reachable.
          </p>
        )}
        <p className="text-xs text-secondary-500 mt-2">
          Action is logged in{" "}
          <code className="px-1 bg-secondary-100 rounded">
            mttr.plug_command_log
          </code>
          .
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setConfirming(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button type="button" onClick={() => void run()} disabled={busy}>
            {busy ? "Working…" : `Yes, turn ${desired.toUpperCase()}`}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default PlugOutletResetButton;
